import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
} from '@mui/material';
import { Chart } from 'react-google-charts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface ISiteVisit {
  siteId: string;
  type: number;
  user: string;
  customerNo: string;
  reportedOn: string;
}

interface VisitDashboardProps {
  visits: ISiteVisit[];
}

const VisitDashboard: React.FC<VisitDashboardProps> = ({ visits }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalVisits = visits.length;
    const entranceCount = visits.filter(v => v.type === 1).length;
    const exitCount = visits.filter(v => v.type === 2).length;
    const uniqueSites = new Set(visits.map(v => v.siteId)).size;
    const uniqueAccounts = new Set(visits.map(v => v.customerNo)).size;

    return {
      totalVisits,
      entranceCount,
      exitCount,
      uniqueSites,
      uniqueUsers: uniqueAccounts,
    };
  }, [visits]);

  // Prepare visits by date chart data
  const visitsByDateData = useMemo(() => {
    const dateMap = new Map<string, number>();

    visits.forEach(visit => {
      const date = new Date(visit.reportedOn).toLocaleDateString();
      dateMap.set(date, (dateMap.get(date) || 0) + 1);
    });

    const data: any[] = [['Date', 'Visits']];
    const sortedDates = Array.from(dateMap.entries()).sort((a, b) =>
      new Date(a[0]).getTime() - new Date(b[0]).getTime()
    );

    sortedDates.forEach(([date, count]) => {
      data.push([date, count]);
    });

    return data.length > 1 ? data : [['Date', 'Visits'], ['No Data', 0]];
  }, [visits]);

  // Prepare visits by hour chart data
  const visitsByHourData = useMemo(() => {
    const hourMap = new Map<number, number>();

    visits.forEach(visit => {
      const hour = new Date(visit.reportedOn).getHours();
      hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
    });

    // Find the first and last hour with visits
    let firstHour = 23;
    let lastHour = 0;

    hourMap.forEach((count, hour) => {
      if (count > 0) {
        if (hour < firstHour) firstHour = hour;
        if (hour > lastHour) lastHour = hour;
      }
    });

    // If no visits, show default range
    if (firstHour > lastHour) {
      firstHour = 0;
      lastHour = 23;
    }

    const data: any[] = [['Hour', 'Visits']];
    for (let i = firstHour; i <= lastHour; i++) {
      const hourLabel = i.toString().padStart(2, '0') + ':00';
      data.push([hourLabel, hourMap.get(i) || 0]);
    }

    return data;
  }, [visits]);

  // Prepare visits by type chart data
  const visitsByTypeData = useMemo(() => {
    const entranceCount = visits.filter(v => v.type === 1).length;
    const exitCount = visits.filter(v => v.type === 2).length;

    return [
      ['Type', 'Count'],
      ['Entrance', entranceCount],
      ['Exit', exitCount],
    ];
  }, [visits]);

  // Prepare visits by site chart data
  const visitsBySiteData = useMemo(() => {
    const siteMap = new Map<string, number>();

    visits.forEach(visit => {
      siteMap.set(visit.siteId, (siteMap.get(visit.siteId) || 0) + 1);
    });

    const data: any[] = [['Site ID', 'Visits']];
    const sortedSites = Array.from(siteMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10); // Top 10 sites

    sortedSites.forEach(([siteId, count]) => {
      data.push([siteId, count]);
    });

    return data.length > 1 ? data : [['Site ID', 'Visits'], ['No Data', 0]];
  }, [visits]);

  const chartOptions = {
    backgroundColor: 'transparent',
    legend: {
      position: isMobile ? 'bottom' : 'right',
      textStyle: { fontSize: isMobile ? 11 : 13 }
    },
    chartArea: {
      left: isMobile ? 50 : 70,
      top: 20,
      width: '85%',
      height: '80%',
      right: 20,
      bottom: 20
    },
    hAxis: {
      textStyle: { fontSize: isMobile ? 11 : 13 },
      slantedText: true,
      slantedTextAngle: isMobile ? 45 : 30,
    },
    vAxis: {
      textStyle: { fontSize: isMobile ? 11 : 13 },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    bars: 'vertical',
    colors: ['#F44336'],
  };

  const pieChartOptions = {
    ...chartOptions,
    colors: ['#2e7d32', '#d32f2f'],
    pieSliceText: 'value',
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 'calc(100vh - 200px)',
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: 'background.default',
        boxSizing: 'border-box'
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 4 }}>
        Visit Dashboard
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%', margin: 0 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <Card sx={{ height: '100%', bgcolor: 'primary.main', color: 'white', minHeight: '120px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summaryStats.totalVisits}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5, fontWeight: 500 }}>
                    Total Visits
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <Card sx={{ height: '100%', bgcolor: 'warning.main', color: 'white', minHeight: '120px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summaryStats.uniqueUsers}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5, fontWeight: 500 }}>
                    Unique Accounts
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <Card sx={{ height: '100%', bgcolor: 'success.main', color: 'white', minHeight: '120px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summaryStats.entranceCount}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5, fontWeight: 500 }}>
                    Entrances
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <Card sx={{ height: '100%', bgcolor: 'error.main', color: 'white', minHeight: '120px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summaryStats.exitCount}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5, fontWeight: 500 }}>
                    Exits
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8, transform: 'rotate(180deg)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2.4 }} sx={{ flex: { md: '1 1 0' }, minWidth: 0 }}>
          <Card sx={{ height: '100%', bgcolor: 'info.main', color: 'white', minHeight: '120px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summaryStats.uniqueSites}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5, fontWeight: 500 }}>
                    Unique Sites
                  </Typography>
                </Box>
                <LocationOnIcon sx={{ fontSize: 48, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ width: '100%', mt: 4, display: 'flex', flexWrap: 'wrap' }}>
        {/* Visits by Date */}
        <Grid size={{ xs: 12, md: 6, xl: 3 }} sx={{ flex: { xl: '0 0 25%' }, maxWidth: { xl: '25%' }, minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', minHeight: '450px', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Visits by Date
            </Typography>
            <Box sx={{ width: '100%', height: '100%' }}>
              <Chart
                chartType="LineChart"
                data={visitsByDateData}
                options={{
                  ...chartOptions,
                  colors: ['#F44336'],
                  curveType: 'function',
                  pointSize: 6,
                  lineWidth: 3,
                  fontSize: isMobile ? 11 : 13,
                  hAxis: {
                    ...chartOptions.hAxis,
                    textStyle: { fontSize: isMobile ? 10 : 12 },
                    slantedText: true,
                    slantedTextAngle: isMobile ? 60 : 45,
                    showTextEvery: 1,
                  },
                  chartArea: {
                    left: isMobile ? 50 : 60,
                    top: 20,
                    width: '80%',
                    height: '75%',
                    right: 20,
                    bottom: isMobile ? 60 : 80
                  } as any,
                }}
                width="100%"
                height={isMobile ? "250px" : isTablet ? "300px" : "450px"}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Visits by Hour */}
        <Grid size={{ xs: 12, md: 6, xl: 3 }} sx={{ flex: { xl: '0 0 25%' }, maxWidth: { xl: '25%' }, minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', minHeight: '450px', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Visits by Hour of Day
            </Typography>
            <Box sx={{ width: '100%', height: '100%' }}>
              <Chart
                chartType="BarChart"
                data={visitsByHourData}
                options={{
                  ...barChartOptions,
                  bars: 'horizontal',
                  fontSize: isMobile ? 11 : 13,
                  hAxis: {
                    ...chartOptions.hAxis,
                    title: 'Visits',
                    textStyle: { fontSize: isMobile ? 11 : 13 },
                  },
                  vAxis: {
                    ...chartOptions.vAxis,
                    title: 'Hour of Day',
                    textStyle: { fontSize: isMobile ? 11 : 13 },
                  },
                  chartArea: {
                    left: isMobile ? 80 : 100,
                    top: 20,
                    width: '70%',
                    height: '85%',
                    right: 20,
                    bottom: 20
                  } as any,
                }}
                width="100%"
                height={isMobile ? "250px" : isTablet ? "300px" : "450px"}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Visits by Type */}
        <Grid size={{ xs: 12, md: 6, xl: 3 }} sx={{ flex: { xl: '0 0 25%' }, maxWidth: { xl: '25%' }, minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', minHeight: '450px', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Visits by Type
            </Typography>
            <Box sx={{ width: '100%', height: '100%' }}>
              <Chart
                chartType="PieChart"
                data={visitsByTypeData}
                options={{
                  ...pieChartOptions,
                  fontSize: isMobile ? 11 : 13,
                }}
                width="100%"
                height={isMobile ? "250px" : isTablet ? "300px" : "450px"}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Top Sites */}
        <Grid size={{ xs: 12, md: 6, xl: 3 }} sx={{ flex: { xl: '0 0 25%' }, maxWidth: { xl: '25%' }, minWidth: 0 }}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', minHeight: '450px', display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Top Sites by Visits
            </Typography>
            <Box sx={{ width: '100%', height: '100%' }}>
              <Chart
                chartType="BarChart"
                data={visitsBySiteData}
                options={{
                  ...barChartOptions,
                  bars: 'horizontal',
                  fontSize: isMobile ? 11 : 13,
                  chartArea: {
                    left: isMobile ? 100 : 150,
                    top: 20,
                    width: '80%',
                    height: '80%',
                    right: 20,
                    bottom: 20
                  } as any,
                }}
                width="100%"
                height={isMobile ? "250px" : isTablet ? "300px" : "450px"}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VisitDashboard;
